import { useDispatch, useSelector } from 'react-redux';
import BTInputField from "@common-components/bt-input-field/bt-input-field";
import { Board } from "@common-models/board";
import { Paper, Typography, Button } from "@mui/material";
import { Stack, Box } from "@mui/system";
import { getAllBoards } from "@common-reducers/BoardsSelectors";
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { recycleActions } from '@common-reducers/RecycleBinReducer';
import { fetchArchivedOrDeletedData } from '@common-reducers/TaskThunks';
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

type FilterOption = string | { name: string; id: string };

export const ArchivedOrDeletedFilterMenu = () => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const filters = useAppSelector(state => state.RecycleBinReducer.filters);
    const prevFiltersRef = useRef(filters);

    const boards: Board[] = useAppSelector(getAllBoards);
    const searchText = useAppSelector((state) => state.RecycleBinReducer.searchText);
    const currentTab = useAppSelector((state) => state.RecycleBinReducer.currentTab);
    const [boardSearchTerm, setBoardSearchTerm] = useState("");
    const filteredBoards = boards.filter(board =>
        (board.name ?? '').toLowerCase().includes(boardSearchTerm.toLowerCase())
    );
    const boardOptions = filteredBoards.map(board => ({ name: board.name ?? '', id: board._id ?? '' }));

    const handleFilterToggle = (field: keyof typeof filters, value: string) => {
        let updatedFilters = { ...filters };

        if (field === 'date') {
            updatedFilters.date = filters.date === value ? '' : value;
        } else {
            const isActive = filters[field].includes(value);
            updatedFilters[field] = isActive
                ? filters[field].filter(item => item !== value)
                : [...filters[field], value];
        }

        dispatch(recycleActions.setFilters(updatedFilters));
    }


    const renderFilterButtons = (field: keyof typeof filters, options: FilterOption[]) => {
        return options.map((option, index) => {
            const displayName = typeof option === 'string' ? option : option.name;
            const value = typeof option === 'string' ? option : option.id;
            const isOutlined = !filters[field].includes(value);

            return (
                <Button
                    key={index}
                    variant={isOutlined ? 'outlined' : 'contained'}
                    sx={{
                        width: field !== "board" ? "150px" : "100%",
                        textTransform: "capitalize",
                        ...(isOutlined ? {
                            borderColor: '#f7f7f7',
                            background: "#f7f7f7",
                            color: "var(--color-text)",
                            '&:hover': {
                                borderColor: '#ebebeb',
                                background: "#ebebeb",
                            }
                        } : {})
                    }}
                    onClick={() => handleFilterToggle(field, value)}
                >
                    {displayName}
                </Button>
            );
        });
    }



    const executeFilter = () => {
        dispatch(recycleActions.setArchivedOrDeletedData([]));
        dispatch(recycleActions.setArchivedOrDeletedBulkItems([]));

        dispatch(fetchArchivedOrDeletedData({
            mode: currentTab,
            itemId: "",
            itemUpdatedAt: "",
            searchQuery: searchText,
            ...filters,
            showLoader: true

        }));
    };

    useEffect(() => {
        if (JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
          executeFilter();
          prevFiltersRef.current = filters;
        }
      }, [filters]);

    return (
        <Paper sx={{ padding: 4, justifyContent: "space-between", width: "650px", height: "400px" }}>
            <Stack direction="row" justifyContent={"space-between"} pb={2}>
                <Box>{t('archiveDialog.filters')}</Box>
                <Button
                    onClick={() => {
                        dispatch(recycleActions.setFilters({
                            type: [],
                            date: '',
                            board: [],
                        }));
                        setBoardSearchTerm("");
                    }}
                    disabled={filters.board.length === 0 && filters.type.length === 0 && filters.date === ""}
                    sx={{
                        fontSize: "12px",
                        textTransform: "capitalize",
                        color: "grey"
                    }}
                >
                    {t('archiveDialog.clearFilters')}
                </Button>
            </Stack>
            <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack spacing={2} alignItems="flex-start">
                    <Typography>{t('archiveDialog.type')}:</Typography>
                    {renderFilterButtons('type', [t('archiveDialog.filterBoard'), t('archiveDialog.filterGroup'), t('archiveDialog.filterTask'), t('archiveDialog.filterSubTask'), t('archiveDialog.filterColumn')])}
                </Stack>
                <Stack spacing={2} alignItems="flex-start">
                    <Typography>{t('archiveDialog.date')}:</Typography>
                    {renderFilterButtons('date', [t('archiveDialog.today'), t('archiveDialog.yesterday'), t('archiveDialog.thisWeek'), t('archiveDialog.lastWeek'), t('archiveDialog.thisMonth')])}
                </Stack>
                <Stack spacing={2} alignItems="flex-start">
                    <Typography>{t('archiveDialog.board')}:</Typography>
                    <BTInputField
                        sx={{ marginInlineStart: '10px' }} type="text"
                        value={boardSearchTerm}
                        onChange={e => setBoardSearchTerm(e.target.value)}
                        placeholder={t('archiveDialog.searchBoards')}
                    />
                    <Stack height={"250px"} width="100%" spacing={2} sx={{
                        overflowY: "scroll",
                        scrollBehavior: "smooth",
                        pr: 1,
                        '&::-webkit-scrollbar': {
                            width: '3px',
                        },
                        '&::-webkit-scrollbar-track': {
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'darkgrey',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: 'grey',
                        },
                    }}>
                        {renderFilterButtons('board', boardOptions)}
                    </Stack>
                </Stack>
            </Stack>
        </Paper>
    );
}

